import React, { useEffect } from "react";
import DialogPropTypes from "./DialogPropTypes";
import addEventListener from "rc-util/lib/Dom/addEventListener";
import KeyCode from "rc-util/lib/KeyCode";
import Button from "components/Button";
import { AppTitle } from "components/LayoutStyles/LayoutStyles";
import cx from "classnames";
import { Container, Body, DialogBox, Closer } from "./DialogStyles";

export interface DialogElementProps extends DialogPropTypes {
  scale?: number;
}

let mousePosition: { x: number; y: number } | null;

const getClickPosition = (e: MouseEvent) => {
  mousePosition = {
    x: e.pageX,
    y: e.pageY,
  };
  // 100ms 内发生过点击事件，则从点击位置动画展示
  // 否则直接 zoom 展示
  // 这样可以兼容非点击方式展开
  setTimeout(() => (mousePosition = null), 100);
};

if (
  typeof window !== "undefined" &&
  window.document &&
  window.document.documentElement
) {
  addEventListener(document.documentElement, "click", getClickPosition);
}

function getScroll(w: any, top?: boolean) {
  let ret = w[`page${top ? "Y" : "X"}Offset`];
  const method = `scroll${top ? "Top" : "Left"}`;
  if (typeof ret !== "number") {
    const d = w.document;
    ret = d.documentElement[method];
    if (typeof ret !== "number") {
      ret = d.body[method];
    }
  }
  return ret;
}

function setTransformOrigin(node: any, value: string) {
  const style = node.style;
  ["Webkit", "Moz", "Ms", "ms"].forEach((prefix: string) => {
    style[`${prefix}TransformOrigin`] = value;
  });
  style[`transformOrigin`] = value;
}

function offset(el: any) {
  const rect = el.getBoundingClientRect();

  const pos = {
    left: rect.left,
    top: rect.top,
  };

  const doc = el.ownerDocument;
  const w = doc.defaultView || doc.parentWindow;
  pos.left += getScroll(w);
  pos.left -= el.offsetWidth / 2;
  pos.top += getScroll(w, true);
  pos.top -= el.offsetHeight / 2;
  return pos;
}

export interface DialogElementProps extends DialogPropTypes {
  scale?: number;
}

const DialogElement = (props: DialogElementProps) => {
  const {
    children,
    visible,
    width,
    scale,
    // title,
    footer,
    closable,
    onCancel,
    onOk,
    keyboard,
    name,
    titleUI,
    className,
    centered,
  } = props;

  const containerRef = React.useRef<any>(null);

  useEffect(() => {
    const dialogNode = containerRef.current;

    if (visible) {
      document.body.style.overflow = "hidden";
      if (mousePosition) {
        setTransformOrigin(dialogNode, "");
        const elOffset = offset(dialogNode);

        setTransformOrigin(
          dialogNode,
          `${mousePosition.x - elOffset.left}px ${
            mousePosition.y - elOffset.top
          }px`
        );

        containerRef.current.focus();
      } else {
        setTransformOrigin(dialogNode, "");
      }
    } else {
      document.body.style.overflow = "";
    }
  }, [visible]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (keyboard && e.keyCode === KeyCode.ESC) {
      e.stopPropagation();
      close(e);
      return;
    }
  };

  const close = (e: any) => {
    if (onCancel) {
      onCancel(e);
    }
  };

  const handleCancel = (e: React.SyntheticEvent<HTMLDivElement>) => {
    if (onCancel) {
      onCancel(e);
    }
  };

  const handleOk = (e: React.SyntheticEvent<HTMLDivElement>) => {
    if (onOk) {
      onOk(e);
    }
  };

  const renderCloser = () => {
    return (
      <Closer type="button" onClick={onCancel} className="modalCloser">
        <i />
      </Closer>
    );
  };

  const renderFooter = () => {
    let footerContent;

    if (footer === undefined) {
      footerContent = (
        <div>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleOk}>Ok</Button>
        </div>
      );
    } else {
      footerContent = footer;
    }

    if (!!footerContent) {
      return <div className="modalFooter">{footerContent}</div>;
    }
    return null;
  };

  return (
    <Container
      className={cx(className ? className : "modal", {
        modal_centered: centered,
      })}
      ref={containerRef}
      width={width}
      style={{
        transform: `scale(${scale})${centered ? " translateY(-50%)" : ""}`,
      }}
      onClick={(e) => {
        // e.stopPropagation();
      }}
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      <DialogBox className={cx("modalBox")}>
        {closable && renderCloser()}
        {
          name && (
            <AppTitle name={name} type="modal">
              {titleUI && titleUI}
            </AppTitle>
          )
          // <Header>
          // <h2>{title}</h2></Header>
        }
        <Body className="modalBody">{children}</Body>
        {renderFooter()}
      </DialogBox>
    </Container>
  );
};

DialogElement.defaultProps = {
  width: 600,
  closable: true,
};

export default DialogElement;
